import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Sparkles, CheckCircle, Shield, CreditCard, ArrowLeft, Tag, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [processing, setProcessing] = useState(false);
  
  // Dynamic pricing state
  const [pricing, setPricing] = useState({ price: 55, access_duration_days: 90, access_duration_months: 3 });
  const [currentPrice, setCurrentPrice] = useState(55.0);
  const [promoActive, setPromoActive] = useState(false);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  useEffect(() => {
    const fetchPricingAndPromo = async () => {
      try {
        const [pricingRes, promoRes] = await Promise.all([
          axios.get(`${API}/pricing`),
          axios.get(`${API}/promo/status`)
        ]);
        const p = pricingRes.data;
        setPricing(p);
        setCurrentPrice(p.price);
        
        if (promoRes.data.is_active) {
          setPromoActive(true);
          setCurrentPrice(promoRes.data.promo_price);
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricingAndPromo();
  }, []);

  const handleValidateDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    setValidatingCode(true);
    try {
      const formData = new FormData();
      formData.append('code', discountCode);
      const res = await axios.post(`${API}/discount/validate`, formData);
      setAppliedDiscount(res.data);
      setCurrentPrice(res.data.discounted_price);
      toast.success(`Discount applied: ${res.data.discount_type === 'percentage' ? `${res.data.discount_value}% off` : `$${res.data.discount_value} off`}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    if (promoActive) {
      axios.get(`${API}/promo/status`).then(res => {
        setCurrentPrice(res.data.is_active ? res.data.promo_price : pricing.price);
      });
    } else {
      setCurrentPrice(pricing.price);
    }
  };

  const applyDiscountUsage = async () => {
    if (appliedDiscount) {
      try {
        const formData = new FormData();
        formData.append('code', appliedDiscount.code);
        await axios.post(`${API}/discount/apply`, formData);
      } catch (err) {
        console.error('Failed to record discount usage:', err);
      }
    }
  };

  const durationLabel = `${pricing.access_duration_months} months`;

  const benefits = [
    'Unlimited research projects',
    'All statistical analyses (descriptive, regression, ANOVA, etc.)',
    'Cronbach\'s Alpha reliability analysis',
    'Qualitative analysis (thematic, sentiment, word clouds)',
    'AI-powered interpretations',
    'Publication-ready exports',
    'Secure cloud storage',
    `${durationLabel} full access`
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-12 h-12 bg-indigo-700 rounded-xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <span className="text-2xl font-serif font-bold text-slate-900">StatsXY</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 mb-4" data-testid="payment-heading">
            Get {durationLabel} Access
          </h1>
          <p className="text-lg text-slate-600">
            Welcome, {user?.full_name}! Complete your purchase to start your research journey.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Benefits Card */}
          <div className="card-marketing" data-testid="benefits-card">
            <h2 className="text-2xl font-serif font-semibold text-slate-900 mb-6">
              What&apos;s Included
            </h2>
            <ul className="space-y-4">
              {benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{benefit}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-3 text-slate-600">
                <Shield className="w-5 h-5 text-indigo-600" />
                <span className="text-sm">Secure payment processing via PayPal</span>
              </div>
            </div>
          </div>

          {/* Payment Card */}
          <div className="card-marketing" data-testid="payment-card">
            <div className="text-center mb-6">
              <div className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
                {promoActive ? 'Special Promotion' : `${durationLabel} Access`}
              </div>
              <div className="mb-2">
                {(promoActive || appliedDiscount) && currentPrice < pricing.price ? (
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl text-slate-400 line-through">${pricing.price}</span>
                    <span className="text-5xl font-serif font-bold text-green-600" data-testid="current-price">${currentPrice.toFixed(2)}</span>
                  </div>
                ) : (
                  <span className="text-5xl font-serif font-bold text-slate-900" data-testid="current-price">${currentPrice.toFixed(2)}</span>
                )}
                <span className="text-slate-500"> / {durationLabel}</span>
              </div>
              {promoActive && !appliedDiscount && (
                <p className="text-green-600 font-medium">Promotional price applied!</p>
              )}
              {appliedDiscount && (
                <p className="text-green-600 font-medium">
                  Code &quot;{appliedDiscount.code}&quot; applied: {appliedDiscount.discount_type === 'percentage' ? `${appliedDiscount.discount_value}% off` : `$${appliedDiscount.discount_value} off`}
                </p>
              )}
              <p className="text-slate-600">One-time payment. {durationLabel} of full access.</p>
            </div>

            {/* Discount Code Input */}
            {!promoActive && (
              <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Have a discount code?
                </label>
                {appliedDiscount ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div>
                      <span className="font-medium text-green-700">{appliedDiscount.code}</span>
                      <span className="text-green-600 text-sm ml-2">
                        ({appliedDiscount.discount_type === 'percentage' ? `${appliedDiscount.discount_value}% off` : `$${appliedDiscount.discount_value} off`})
                      </span>
                    </div>
                    <button onClick={handleRemoveDiscount} className="text-red-500 hover:text-red-700 p-1" data-testid="remove-discount-btn">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1"
                      data-testid="discount-code-input"
                    />
                    <Button
                      onClick={handleValidateDiscount}
                      disabled={validatingCode || !discountCode.trim()}
                      variant="outline"
                      data-testid="apply-discount-btn"
                    >
                      {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {promoActive && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                Discount codes cannot be used during promotional periods.
              </div>
            )}

            <div className="space-y-4">
              <PayPalScriptProvider options={{ 
                clientId: PAYPAL_CLIENT_ID,
                currency: "USD",
                components: "buttons,funding-eligibility"
              }}>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <CreditCard className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-700">Pay with Credit Card</span>
                  </div>
                  <PayPalButtons
                    key={currentPrice}
                    style={{ layout: "vertical", color: "black", shape: "pill", label: "pay" }}
                    disabled={processing}
                    fundingSource="card"
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [{
                          amount: { value: currentPrice.toFixed(2), currency_code: "USD" },
                          description: `StatsXY ${durationLabel} Access`
                        }]
                      });
                    }}
                    onApprove={async (data, actions) => {
                      setProcessing(true);
                      try {
                        const details = await actions.order.capture();
                        await applyDiscountUsage();
                        await axios.post(
                          `${API}/subscription/activate`,
                          { order_id: details.id, status: details.status },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        updateUser({ subscription_status: 'active' });
                        toast.success('Payment successful! Welcome to StatsXY!');
                        navigate('/dashboard');
                      } catch (err) {
                        toast.error('Payment processing failed');
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    onError={() => {
                      toast.error('Payment error. Please try again.');
                    }}
                  />
                  <p className="text-xs text-center text-slate-500 mt-3">
                    Secure payment via PayPal
                  </p>
                </div>
              </PayPalScriptProvider>

              <button
                onClick={() => navigate(-1)}
                className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-slate-900 py-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
